import { useMemo, useState } from "react";
import { Button, Empty, Input, Select, Table, Tag, Typography, type TableProps } from "antd";
import type { UserAccountAdminListItem } from "./userAccountAdminApi";
import { label } from "../../lib/labels";

export type StatusFilter = UserAccountAdminListItem["status"] | undefined;

const ACCOUNT_STATUS_LABEL: Readonly<Record<NonNullable<StatusFilter>, string>> = {
  INACTIVE: "Chưa kích hoạt",
  ACTIVE: "Đang hoạt động",
  LOCKED: "Đã khóa",
};

function statusTag(status: UserAccountAdminListItem["status"], displayName?: string | null) {
  const text = label(status, displayName ?? ACCOUNT_STATUS_LABEL[status]);
  if (status === "ACTIVE") return <Tag color="success">{text}</Tag>;
  if (status === "LOCKED") return <Tag color="error">{text}</Tag>;
  return <Tag>{text}</Tag>;
}

export function UserAccountTable({
  data,
  loading,
  selectedId,
  pagination,
  filters,
  organizationOptions,
  onSelectRow,
  onChangeSearch,
  onChangeStatus,
  onChangeOrganization,
  onChangePage,
  onClearFilters,
}: {
  readonly data: readonly UserAccountAdminListItem[];
  readonly loading: boolean;
  readonly selectedId: string | null;
  readonly pagination: { current: number; pageSize: number; total: number };
  readonly filters: { q: string; status: StatusFilter; organizationId: string | undefined };
  readonly organizationOptions: readonly { value: string; label: string }[];
  readonly onSelectRow: (id: string) => void;
  readonly onChangeSearch: (q: string) => void;
  readonly onChangeStatus: (status: StatusFilter) => void;
  readonly onChangeOrganization: (organizationId: string | undefined) => void;
  readonly onChangePage: (page: number, pageSize: number) => void;
  readonly onClearFilters: () => void;
}) {
  const [searchDraft, setSearchDraft] = useState(filters.q);

  const columns: TableProps<UserAccountAdminListItem>["columns"] = [
    {
      title: "Họ và tên",
      dataIndex: "personDisplayName",
      render: (value, row) => (
        <Button type="link" className="account-row-trigger" onClick={() => onSelectRow(row.id)}>
          {value as string}
        </Button>
      ),
    },
    { title: "Tên đăng nhập", dataIndex: "username" },
    {
      title: "Đơn vị công tác",
      key: "homeOrganization",
      render: (_value, row) => row.homeOrganization.name,
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      render: (value: string, record: UserAccountAdminListItem) =>
        statusTag(
          value as UserAccountAdminListItem["status"],
          (record as Record<string, unknown>).statusDisplayName as string | undefined,
        ),
    },
    {
      title: "Liên kết đăng nhập",
      key: "identityLinked",
      render: (_value, row) => (
        <Typography.Text type="secondary">{row.identityLinked ? "Đã liên kết" : "Chưa liên kết"}</Typography.Text>
      ),
    },
  ];

  const emptyNode = useMemo(() => {
    if (loading || data.length > 0) return undefined;
    const hasActiveFilters =
      filters.q.trim() !== "" || filters.status !== undefined || filters.organizationId !== undefined;
    return hasActiveFilters ? (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <div className="account-empty-action">
            <span>Không tìm thấy tài khoản phù hợp bộ lọc</span>
            <Button
              onClick={() => {
                setSearchDraft("");
                onClearFilters();
              }}
            >
              Xóa bộ lọc
            </Button>
          </div>
        }
      />
    ) : (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Chưa có tài khoản người dùng" />
    );
  }, [data.length, filters, loading, onClearFilters]);

  return (
    <>
      <div className="user-accounts-toolbar" role="search">
        <Input.Search
          className="account-search"
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          onSearch={(value) => onChangeSearch(value.trim())}
          enterButton="Tìm"
          aria-label="Tìm theo tên người dùng hoặc tên đăng nhập"
          placeholder="Tên người dùng hoặc tên đăng nhập"
        />
        <Select<StatusFilter>
          className="account-filter"
          aria-label="Lọc trạng thái"
          value={filters.status}
          onChange={(value) => onChangeStatus(value)}
          allowClear
          placeholder="Tất cả trạng thái"
          virtual={false}
          options={[
            { value: "INACTIVE", label: ACCOUNT_STATUS_LABEL.INACTIVE },
            { value: "ACTIVE", label: ACCOUNT_STATUS_LABEL.ACTIVE },
            { value: "LOCKED", label: ACCOUNT_STATUS_LABEL.LOCKED },
          ]}
        />
        <Select<string | undefined>
          className="account-filter"
          aria-label="Lọc đơn vị công tác"
          value={filters.organizationId}
          onChange={(value) => onChangeOrganization(value)}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Tất cả đơn vị"
          virtual={false}
          options={[...organizationOptions]}
        />
      </div>
      <Table<UserAccountAdminListItem>
        className="user-accounts-table"
        aria-label="Danh sách tài khoản người dùng"
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={[...data]}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
          onChange: (page, pageSize) => onChangePage(page, pageSize),
        }}
        locale={{ emptyText: emptyNode }}
        rowClassName={(row) => (row.id === selectedId ? "account-row-selected" : "")}
        onRow={(row) => ({
          onClick: () => onSelectRow(row.id),
          "aria-current": row.id === selectedId ? "true" : undefined,
        })}
      />
    </>
  );
}